import {DataStatus} from 'platform/components';

import {defaultTo} from 'ramda';

import {useGetAuditDataQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInQuery,
  useGetServiceHandOverQuery,
} from '@dms/api/workshop';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useContactPersons} from '../../../hooks/useContactPersons';
import {ServiceCaseOrder} from '../../../types/OrderType';
import {NotificationCategory} from '../types/EmailSmsNotificationForm';
import {EmailSmsNotification} from './EmailSmsNotification';

interface EmailSmsNotificationBoundaryProps extends RequiredTestIdProps {
  orderId?: string;
  serviceCaseId: string;
  dialogId: string;
  // Only contactInformation is consumed downstream, which the order-list summary carries.
  serviceOrder?: ServiceCaseOrder;
  defaultCategory?: NotificationCategory;
}

export function EmailSmsNotificationBoundary(props: EmailSmsNotificationBoundaryProps) {
  const serviceCaseQuery = useGetMetadaServiceCaseQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const serviceCheckinQuery = useGetServiceCheckInQuery({serviceCaseId: props.serviceCaseId});
  const serviceHandoverQuery = useGetServiceHandOverQuery({serviceCaseId: props.serviceCaseId});

  const checkingInspectionId = defaultTo('', serviceCheckinQuery.data?.inspectionId?.[0]);
  const handoverInspectionId = defaultTo('', serviceHandoverQuery.data?.inspectionId?.[0]);

  const checkinAuditDataQuery = useGetAuditDataQuery(
    {auditId: checkingInspectionId},
    {skip: !checkingInspectionId, refetchOnMountOrArgChange: true}
  );
  const handoverAuditDataQuery = useGetAuditDataQuery(
    {auditId: handoverInspectionId},
    {skip: !handoverInspectionId, refetchOnMountOrArgChange: true}
  );

  const currentUserQuery = useGetCurrentUserInfoQuery();
  const selectedLang = defaultTo('en', currentUserQuery.data?.settings.language as string);

  const {
    isLoading: isContactPersonLoading,
    isError: isContactPersonError,
    contactPersons,
    primaryContactIds,
  } = useContactPersons(props.serviceCaseId);

  const isLoading =
    serviceCaseQuery.isLoading ||
    serviceCheckinQuery.isLoading ||
    serviceHandoverQuery.isLoading ||
    checkinAuditDataQuery.isLoading ||
    handoverAuditDataQuery.isLoading ||
    currentUserQuery.isLoading ||
    isContactPersonLoading;

  const isError =
    serviceCaseQuery.isError ||
    serviceCheckinQuery.isError ||
    serviceHandoverQuery.isError ||
    checkinAuditDataQuery.isError ||
    handoverAuditDataQuery.isError ||
    currentUserQuery.isError ||
    isContactPersonError;

  const serviceCaseState = serviceCaseQuery.data?.state;

  const checkinAudit = checkinAuditDataQuery?.data?.find(
    (item) => item.id === checkingInspectionId
  );
  const isCheckinFinished = checkinAudit?.state === 'finished_audit';

  const handoverAudit = handoverAuditDataQuery?.data?.find(
    (item) => item.id === handoverInspectionId
  );
  const isHandoverFinished = handoverAudit?.state === 'finished_audit';

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={200}>
      <EmailSmsNotification
        orderId={props.orderId}
        serviceCaseId={props.serviceCaseId}
        dialogId={props.dialogId}
        serviceOrder={props.serviceOrder}
        serviceCaseState={serviceCaseState || undefined}
        isCheckinFinished={isCheckinFinished}
        isHandoverFinished={isHandoverFinished}
        contactPersons={contactPersons}
        primaryContactIds={primaryContactIds}
        checkinInspectionId={checkingInspectionId}
        handoverInspectionId={handoverInspectionId}
        selectedLang={selectedLang}
        defaultCategory={props.defaultCategory}
        data-testid={suffixTestId('sms-email-boundary', props)}
      />
    </DataStatus>
  );
}
